import type { MeasureDocumentAdapter } from '@/adapters/measure-document-adapter';
import type { DocAiDocument } from '@/types/api-response/doc-ai-types';
import { createMeasureDocumentAdapter } from '@/adapters/measure-document-adapter';
import { measureToDocAiDocument } from '@/utils/measure-to-doc-ai';

/**
 * `measure_ids` on the list endpoint and `document_ids` on resolve-ids are both
 * capped at 500 per request, so larger sets are split. Luma can cite well past
 * that when a question spans a corpus.
 */
const BATCH_SIZE = 500;

/**
 * Split a set into request-sized batches.
 *
 * A size below 1 would leave the loop never advancing — an infinite loop growing
 * an array until the tab dies. `batchSize` is a test seam rather than caller
 * input, so that can only be a programming error, and failing loudly beats
 * silently clamping it.
 *
 * The endpoint's own 500-item cap is deliberately not re-checked here. That is
 * the server's constant to enforce, and a copy of it on this side would only
 * drift from the original.
 */
function inBatches<T>(items: T[], batchSize: number): T[][] {
   if (!Number.isInteger(batchSize) || batchSize < 1)
      throw new RangeError(`batchSize must be a positive integer, got ${batchSize}`);

   const batches: T[][] = [];
   for (let i = 0; i < items.length; i += batchSize) {
      batches.push(items.slice(i, i + batchSize));
   }
   return batches;
}

export interface FetchDocumentsByMeasureIdsOptions {
   adapter?: MeasureDocumentAdapter;
   batchSize?: number;
}

/**
 * Fetch full documents by `Measure.id`, deduplicated and flattened.
 *
 * Keyed by measure id rather than NMED docid: Luma's references now carry
 * `measure_id`, and measure ids are unique across tenants where a docid is only
 * unique within NMED.
 *
 * Rows come back in the API's order, not the caller's — batches resolve
 * concurrently and the list endpoint orders by event date.
 */
export async function fetchDocumentsByMeasureIds(
   measureIds: number[],
   options: FetchDocumentsByMeasureIdsOptions = {},
): Promise<DocAiDocument[]> {
   const unique = [...new Set(measureIds)].filter(id => Number.isFinite(id));
   if (unique.length === 0)
      return [];

   const adapter = options.adapter ?? createMeasureDocumentAdapter();
   const batchSize = options.batchSize ?? BATCH_SIZE;

   const batches = inBatches(unique, batchSize);

   const results = await Promise.all(batches.map(batch => adapter.listByMeasureIds(batch)));
   return results.flat().map(measureToDocAiDocument);
}

export interface ResolveMeasureIdsOptions {
   adapter?: MeasureDocumentAdapter;
   batchSize?: number;
}

/**
 * Resolve document identifiers to `Measure.id`, keyed by the value passed in.
 *
 * The bridge for callers that only hold an NMED docid — a Luma citation, a
 * project membership row written under the older convention — while every route
 * that serves a document is keyed by the measure id. Either convention is
 * accepted per value; the caller doesn't have to know which one it holds.
 *
 * An identifier the caller cannot reach is absent from the result rather than
 * mapped to 0, so "not in the map" and "resolved to nothing" are one case.
 */
export async function resolveMeasureIds(
   documentIds: string[],
   options: ResolveMeasureIdsOptions = {},
): Promise<Record<string, number>> {
   const unique = [...new Set(documentIds)].filter(Boolean);
   if (unique.length === 0)
      return {};

   const adapter = options.adapter ?? createMeasureDocumentAdapter();
   const batchSize = options.batchSize ?? BATCH_SIZE;

   const batches = inBatches(unique, batchSize);

   const results = await Promise.all(batches.map(batch => adapter.resolveMeasureIds(batch)));
   return Object.assign({}, ...results) as Record<string, number>;
}

/**
 * The `Measure.id` for one document identifier, or null when it resolves to
 * nothing the caller can reach.
 *
 * Null rather than 0 so a caller has to handle the miss: 0 is the "no measure"
 * sentinel the viewer state carries, and letting it flow silently is how a
 * request for document 0 gets made in the first place.
 */
export async function measureIdForDocId(
   documentId: string,
   options: ResolveMeasureIdsOptions = {},
): Promise<number | null> {
   if (!documentId)
      return null;
   const resolved = await resolveMeasureIds([documentId], options);
   return resolved[documentId] ?? null;
}
